import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sendPurchaseEvent, sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { PLANS } from '../../analytics/eCommerce';
import {
  selectStripeNonce,
  selectBraintreeNonce,
  sendUpsellStripePurchase,
  sendUpsellBraintreePurchase
} from '../checkout/checkoutSlice';
import { selectPlan, selectUpsellPlan, setUpsellPlan } from './plansSlice';
import { selectAnalyticsData, selectEventsData } from '../events/eventsSlice';
import { selectPaymentProvider, selectToken } from '../signup/signupSlice';
import { setCheckoutLoader } from '../loader/loaderSlice';
import { WhiteCheckIcon } from './styled';
import UIButton from '../../UI/UIButton';
import { tabletMinWidth } from '../../mixins';

const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const Title = styled.h1`
  font-weight: 800;
  font-size: 32px;
  line-height: 38px;
  color: #323232;
  margin: 24px 0 16px 0;
  padding: 0;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin-top: 64px;
  }
`;

const Descr = styled.h2`
  font-size: 16px;
  line-height: 19px;
  color: #323232;
  white-space: pre-wrap;
  margin: 0 0 33px 0;
  padding: 0;
`;

const DiscountLabel = styled.div`
  background-color: rgba(245, 138, 60, 0.1);
  border: 1px solid #F58A3C;
  border-radius: 12px;
  box-sizing: border-box;
  padding: ${isArabic ? '22px 84px 22px 16px' : '22px 16px 22px 84px'};
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #848484;
  text-align: ${textAlign};
  position: relative;

  span {
    font-weight: 700;
  }

  &:after {
    content: attr(data-text);
    display: block;
    width: 52px;
    height: 67px;
    background: url(${"../assets/upsell/discount-label.svg"}) top / contain no-repeat;
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
    color: #fff;
    text-align: center;
    padding-top: 8px;
    position: absolute;
    top: -9px;
    ${textAlign}: 16px;
    //text-shadow: 0 0 4px #3293ec;
  }
`;

const PlansBlock = styled.div`
  & > div {
    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }
`;

const Plan = styled.div`
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
  border: 1px solid ${({ active }) => (active ? '#F58A3C' : '#D4DAE0')};
  ${({ active }) => active && 'box-shadow: 0 0 3px 0 #E6F9F6;'}
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: ${({ active }) => (active ? '#F58A3C' : '#848484')};
  text-align: left;
  border-radius: 12px;
  box-sizing: border-box;
`;

const PlanCheck = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  margin-right: 16px;
  margin-left: 16px;
  margin-${textAlign}: 0;
  background: ${({ active }) => (active ? '#F58A3C' : '#fff')};
  border: 1px solid ${({ active }) => (active ? '#F58A3C' : '#ADADAD')};
  transition: all 0.1s linear;
  transition-property: background-color, border-color;
`;

const PlanTitle = styled.div`
  flex-grow: 1;
  padding-right: 16px;
  padding-left: 16px;
  padding-${textAlign}: 0;
  white-space: pre-wrap;
`;

const PlanPrice = styled.div`
  font-weight: 700;
`;

const FeaturesBlock = styled.div`
  margin: 32px 0;

  & > div {
    &:not(:last-child) {
      margin-bottom: 25px;
    }

    &:nth-child(even) {
      flex-direction: row-reverse;
    }
  }
`;

const Feature = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (min-width: ${tabletMinWidth}px) {
    align-items: center;
  }
`;

const FeatureImg = styled.div`
  width: calc(50% - 9px);
  height: 170px;
  background: ${({img}) => `url(../assets/upsell/${img}.png) center/contain no-repeat`};
`;

const FeatureText = styled.div`
  width: calc(50% - 9px);
  text-align: left;

  div {
    font-size: 14px;
    line-height: 17px;
    color: #323232;

    &:first-child {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      margin-bottom: 8px;
    }
  }
`;

const CloseUpsell = styled.div`
  margin-top: 16px;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #848484;
  text-decoration: underline;
  padding-bottom: 124px;

  @media screen and (min-width: ${tabletMinWidth}px) {
    padding-bottom: 84px;
  }
`;

const UpsellScreen = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = history.push;
  const amplitudeEventsData = useSelector(selectEventsData);
  const plan = useSelector(selectPlan);
  const upsellPlan = useSelector(selectUpsellPlan);
  const stripeNonce = useSelector(selectStripeNonce);
  const braintreeNonce = useSelector(selectBraintreeNonce);
  const token = useSelector(selectToken);
  const analyticsParams = useSelector(selectAnalyticsData);
  const eventsData = useSelector(selectEventsData);
  const paymentProvider = useSelector(selectPaymentProvider);
  const useBraintree = paymentProvider === 'braintree';

  useEffect(() => {
    sendAnalyticsEvents(EVENTS.upsellView, amplitudeEventsData);
  }, [amplitudeEventsData]);

  const onContinueClick = (purchasePlan = upsellPlan) => {
    const { periodPrice, price, productId, currency } = PLANS[purchasePlan];
    const { fbclid, gclid, ttclid } = eventsData;
    const upsellPlanParams = {
      periodPrice,
      price,
      Value: price,
      value: Math.round(price * 100),
      product_id: productId,
    };
    const updatedEventsData = Object.assign({}, eventsData, upsellPlanParams)

    window?.ttq?.track('PlaceAnOrder', {});
    sendAnalyticsEvents(EVENTS.upsellTapped, updatedEventsData);
    const onSuccess = (subscriptionId, event_id) => {
      sendAnalyticsEvents(EVENTS.upsellPurchased, updatedEventsData);
      sendPurchaseEvent(
        periodPrice,
        currency,
        purchasePlan,
        subscriptionId,
        event_id,
        gclid,
        fbclid ? 'fb' : ttclid ? 'ttq' : ''
      );
    };

    const onError = (props) => {
      console.warn('Upsell purchase error:', props);
      sendAnalyticsEvents(EVENTS.upsellFailed, updatedEventsData);
    };

    if (useBraintree) {
      dispatch(sendUpsellBraintreePurchase(braintreeNonce, purchasePlan, token, analyticsParams, updatedEventsData, onSuccess, onError));
    } else {
      dispatch(sendUpsellStripePurchase(stripeNonce, purchasePlan, token, analyticsParams, updatedEventsData, onSuccess, onError));
    }
    setTimeout(() => {
      dispatch(setCheckoutLoader({show: false, type: ''}));
    }, 700);
    navigate('/success');
  };

  const upsellFullPlans = useMemo(() => {
    return {
      'fm-monthly-39.99': 'fm-monthly-upsell-999',
      'fm-3month-59.99': 'fm-3month-upsell-999',
      'fm-6month-71.76': 'fm-6month-upsell-999',
    };
  }, []);

  const upsellPlans = useMemo(() => {
    return {
      'fm-monthly-39.99': [
        {
          id: "fm-monthly-upsell-799",
          title: t("communicate_in_chat"),
          price: "$7.99",
        },
        {
          id: "fm-monthly-upsell-599",
          title: t("use_familo"),
          price: "$5.99",
        },
      ],
      'fm-3month-59.99': [
        {
          id: "fm-3month-upsell-799",
          title: t("communicate_in_chat"),
          price: "$7.99",
        },
        {
          id: "fm-3month-upsell-599",
          title: t("use_familo"),
          price: "$5.99",
        },
      ],
      'fm-6month-71.76': [
        {
          id: "fm-6month-upsell-799",
          title: t("communicate_in_chat"),
          price: "$7.99",
        },
        {
          id: "fm-6month-upsell-599",
          title: t("use_familo"),
          price: "$5.99",
        },
      ],
    };
  }, [t]);

  const currentFullPlan = useMemo(() => upsellFullPlans[plan],[upsellFullPlans, plan]);

  const currentPlans = useMemo(() => upsellPlans[plan],[upsellPlans, plan]);

  useEffect(() => {
    if (!currentPlans) return;
    dispatch(setUpsellPlan(currentPlans[0].id))
  }, [dispatch, currentPlans]);

  const handleSetPlan = (id) => {
    if (id === upsellPlan) return;
    if (upsellPlan === currentFullPlan) {
      const plan = currentPlans.find(p => p.id !== id).id;
      dispatch(setUpsellPlan(plan));
      return;
    }
    dispatch(setUpsellPlan(currentFullPlan));
  };

  const renderOffer = (planInfo, index) => {
    const { id, title, price } = planInfo;
    const isActive = (upsellPlan === id || upsellPlan === currentFullPlan);

    return (
      <Plan
        active={isActive}
        onClick={() => handleSetPlan(id)}
        id={"upsell-plan-button-" + (index + 1)}
        key={id}
      >
        <PlanCheck active={isActive}>{WhiteCheckIcon}</PlanCheck>
        <PlanTitle>{title}</PlanTitle>
        <PlanPrice>{price}</PlanPrice>
      </Plan>
    );
  };

  const renderFeature = (title, i) => (
    <Feature key={i}>
      <FeatureImg img={`feature${i+1}`}/>
      <FeatureText>
        <div>{t(title)}</div>
        <div>{t(`${title}_descr`)}</div>
      </FeatureText>
    </Feature>
  );

  const continueButton = ({ text, color, margin, id }) => (
    <UIButton
      onClick={() => onContinueClick(text === 'accept_all' ? currentFullPlan : upsellPlan)}
      displayStickyBtn={false}
      displayStubBtn={false}
      customBeforeHeight={false}
      customColor={color}
      customMargin={margin}
      customId={id}
    >
      {t(text)}
    </UIButton>
  );

  const closeUpsell = () => {
    sendAnalyticsEvents(EVENTS.upsellTappedClose, eventsData);
    navigate('/success');
  };

  return (
    <>
      <Title>{t('extra_features')}</Title>
      <Descr>{t('get_more_func')}</Descr>
      <DiscountLabel
        data-text={t('30_off')}
        dangerouslySetInnerHTML={{ __html: t('choose_all') }}
        onClick={() => handleSetPlan(currentFullPlan)}
      />
      <PlansBlock>{currentPlans.map(renderOffer)}</PlansBlock>
      {continueButton({ text: 'accept_all', id: 'accept-all' })}
      {continueButton({ text: 'accept_selected', color: '#CCCCCC', margin: '16px 0 0', id: 'accept-selected' })}
      <FeaturesBlock>{['communicate_in_chat', 'use_familo'].map(renderFeature)}</FeaturesBlock>
      {continueButton({ text: 'lets_go', margin: '0', id: 'get-plan' })}
      <CloseUpsell onClick={closeUpsell} id='upsell-skip'>{t('no_thank_you')}</CloseUpsell>
    </>
  )
}

export default UpsellScreen;